import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Divider,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Google as GoogleIcon
} from '@mui/icons-material';
import { auth } from '../firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider
} from 'firebase/auth';
import { usePlatformSettings } from '../hooks/usePlatformSettings';

const Auth = ({ onSuccess, onCancel }) => {
  const { settings: platformSettings } = usePlatformSettings();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    if (!email || !password) {
      setError('Please fill in all fields');
      return false;
    }

    if (!isLogin && password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }

    return true;
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setLoading(true);

    try {
      if (isLogin) {
        // Sign in existing user
        await signInWithEmailAndPassword(auth, email, password);
        console.log('✅ Signed in successfully');
      } else {
        // Create new user
        await createUserWithEmailAndPassword(auth, email, password);
        console.log('✅ Account created successfully');
      }
      
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Auth error:', err);
      
      // User-friendly error messages
      switch (err.code) {
        case 'auth/email-already-in-use':
          setError('Email already in use. Try logging in instead.');
          break;
        case 'auth/invalid-email':
          setError('Invalid email address');
          break;
        case 'auth/user-not-found':
          setError('No account found. Try signing up instead.');
          break;
        case 'auth/wrong-password':
          setError('Incorrect password');
          break;
        case 'auth/too-many-requests':
          setError('Too many failed attempts. Try again later.');
          break;
        default:
          setError(err.message || 'Authentication failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setError('');
    setLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      
      // Try silent sign-in first for returning users
      // This minimizes the number of screens they see
      provider.setCustomParameters({
        prompt: 'select_account' // Let Google decide optimal UX
      });
      
      await signInWithPopup(auth, provider);
      console.log('✅ Signed in with Google');
      
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Google auth error:', err);
      
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Sign-in cancelled');
      } else if (err.code === 'auth/unauthorized-domain') {
        setError('Domain not authorized. Please contact support.');
      } else {
        setError('Google sign-in failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4
      }}
    >
      <Paper
        sx={{
          maxWidth: 450,
          width: '100%',
          p: 4,
          backgroundColor: '#171717',
          border: '1px solid #262626',
          borderRadius: '12px',
          mx: 2
        }}
      >
        {/* Header */}
        <Typography
          variant="h4"
          sx={{
            fontFamily: 'monospace',
            color: '#fafafa',
            mb: 1,
            textAlign: 'center'
          }}
        >
          {isLogin ? 'Welcome Back' : 'Create Account'}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: '#737373',
            mb: 4,
            textAlign: 'center'
          }}
        >
          {isLogin
            ? `Sign in to access your ${platformSettings.platformName} reports`
            : 'Sign up to get your first report'}
        </Typography>

        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            onClose={() => setError('')}
            sx={{
              mb: 3,
              backgroundColor: '#0a0a0a',
              border: '1px solid #ef4444',
              color: '#fafafa'
            }}
          >
            {error}
          </Alert>
        )}

        {/* Google Sign In */}
        <Button
          fullWidth
          variant="outlined"
          size="large"
          startIcon={<GoogleIcon />}
          onClick={handleGoogleAuth}
          disabled={loading}
          sx={{
            mb: 3,
            py: 1.5,
            borderColor: '#262626',
            color: '#fafafa',
            '&:hover': {
              borderColor: '#525252',
              backgroundColor: '#0a0a0a'
            }
          }}
        >
          {loading ? 'Signing in...' : 'Continue with Google'}
        </Button>

        <Divider sx={{ my: 3, borderColor: '#262626' }}>
          <Typography variant="body2" sx={{ color: '#737373', px: 2 }}>
            or
          </Typography>
        </Divider>

        {/* Email/Password Form */}
        <form onSubmit={handleEmailAuth}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#0a0a0a',
                '& fieldset': { borderColor: '#262626' },
                '&:hover fieldset': { borderColor: '#525252' },
                '&.Mui-focused fieldset': { borderColor: '#fafafa' }
              },
              '& .MuiInputLabel-root': { color: '#737373' },
              '& .MuiInputBase-input': { color: '#fafafa' }
            }}
          />

          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    sx={{ color: '#737373' }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#0a0a0a',
                '& fieldset': { borderColor: '#262626' },
                '&:hover fieldset': { borderColor: '#525252' },
                '&.Mui-focused fieldset': { borderColor: '#fafafa' }
              },
              '& .MuiInputLabel-root': { color: '#737373' },
              '& .MuiInputBase-input': { color: '#fafafa' }
            }}
          />

          {!isLogin && (
            <TextField
              fullWidth
              label="Confirm Password"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#0a0a0a',
                  '& fieldset': { borderColor: '#262626' },
                  '&:hover fieldset': { borderColor: '#525252' },
                  '&.Mui-focused fieldset': { borderColor: '#fafafa' }
                },
                '& .MuiInputLabel-root': { color: '#737373' },
                '& .MuiInputBase-input': { color: '#fafafa' }
              }}
            />
          )}

          {/* Submit Button */}
          <Button
            fullWidth
            type="submit"
            variant="contained"
            size="large"
            disabled={loading}
            sx={{
              py: 1.5,
              mb: 2,
              backgroundColor: '#fafafa',
              color: '#0a0a0a',
              fontFamily: 'monospace',
              '&:hover': {
                backgroundColor: '#e5e5e5'
              },
              '&.Mui-disabled': {
                backgroundColor: '#262626',
                color: '#525252'
              }
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: '#525252' }} />
            ) : isLogin ? (
              'Sign In'
            ) : (
              'Create Account'
            )}
          </Button>
        </form>

        {/* Toggle Login/Signup */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography variant="body2" sx={{ color: '#737373' }}>
            {isLogin ? "Don't have an account? " : 'Already have an account? '}
            <Button
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
                setPassword('');
                setConfirmPassword('');
              }}
              disabled={loading}
              sx={{
                color: '#fafafa',
                textDecoration: 'underline',
                textTransform: 'none',
                p: 0,
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline'
                }
              }}
            >
              {isLogin ? 'Sign up' : 'Sign in'}
            </Button>
          </Typography>
        </Box>

        {/* Cancel Button */}
        {onCancel && (
          <Button
            fullWidth
            variant="outlined"
            onClick={onCancel}
            disabled={loading}
            sx={{
              color: '#737373',
              borderColor: '#262626',
              '&:hover': {
                borderColor: '#525252',
                backgroundColor: '#0a0a0a'
              }
            }}
          >
            Cancel
          </Button>
        )}
      </Paper>
    </Box>
  );
};

export default Auth;

